import { useState } from "react";
import { motion } from "framer-motion";
import { EyeOff, Timer, MousePointerClick, BadgeCheck, Factory, FileBarChart, Info } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n));

// Single source of truth for every calculator figure below.
const COST_PER_CUP = 4.2; // ₹ per cup incl. full-colour print, at pilot volumes
const IMPRESSIONS_PER_CUP = 12; // people who see one cup across its use
const HOLD_MINUTES_PER_CUP = 35; // average time a cup is in a customer's hand

const steps = [
  { icon: MousePointerClick, title: "Campaign Selection", text: "Pick your pilot size, budget and target areas — we map the right tapris, cafés & IT parks to your audience." },
  { icon: BadgeCheck, title: "Artwork Approval", text: "Our studio adapts your artwork for cups, tissues & bottles. You approve a free 3D proof before anything prints." },
  { icon: Factory, title: "Production & Distribution", text: "We manufacture in-house and distribute to our vendor network — serving begins within 15 days." },
  { icon: FileBarChart, title: "Campaign Reporting", text: "You receive a placement and estimated-reach report when the campaign wraps." },
];

const ASSUMPTIONS = [
  `₹${COST_PER_CUP.toFixed(2)} per cup — indicative cup + print cost at pilot volumes; your final rate is confirmed on the quote`,
  `${IMPRESSIONS_PER_CUP} people see each cup — the person holding it plus colleagues, family & passers-by over its use`,
  `${HOLD_MINUTES_PER_CUP} minutes average hold time per cup — typical chai or coffee drinking session`,
  "All exposure figures are estimates, not guarantees — actual reach varies by vendor location and footfall",
];

export const AdReach = () => {
  const [cups, setCups] = useState(10000);
  const investment = cups * COST_PER_CUP;
  const impressions = cups * IMPRESSIONS_PER_CUP;
  const exposureHours = (cups * HOLD_MINUTES_PER_CUP) / 60;
  const costPerImpression = investment / impressions;

  return (
    <section id="reach" className="relative py-24 sm:py-32 overflow-hidden" data-testid="ad-reach-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#0D9488] mb-4" data-testid="reach-eyebrow">
            The Offline Ad Revolution
          </p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#2E1065] leading-tight" data-testid="reach-headline">
            Cheaper than a boosted post.{" "}
            <span className="text-[#FF5A36] italic">Impossible to scroll past.</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#2E1065] rounded-[2rem] p-8 sm:p-10 text-[#FAF7F2] overflow-hidden grain"
            data-testid="cup-ads-card"
          >
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#FF5A36]/30 blur-2xl" />
            <div className="flex items-center gap-3 mb-8">
              <span className="bg-[#FF5A36] text-white text-xs font-black uppercase tracking-widest rounded-full px-3 py-1.5">Cup Connect Ads</span>
            </div>
            <ul className="space-y-5">
              {[
                [`${HOLD_MINUTES_PER_CUP} min`, "average hold time per cup"],
                ["0%", "ad-block rate — ever"],
                [`${IMPRESSIONS_PER_CUP}×`, "people see each cup in circulation"],
                [`₹${costPerImpression.toFixed(2)}`, "estimated cost per impression*"],
              ].map(([v, l]) => (
                <li key={l} className="flex items-baseline gap-4 border-b border-white/10 pb-4">
                  <span className="font-display font-black text-3xl text-[#FBBF24] shrink-0 w-28">{v}</span>
                  <span className="text-sm text-[#FAF7F2]/75">{l}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-[#FAF7F2]/50">*Based on the assumptions listed in the calculator below.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white rounded-[2rem] p-8 sm:p-10 border border-[#2E1065]/10"
            data-testid="social-ads-card"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="bg-[#2E1065]/10 text-[#2E1065]/60 text-xs font-black uppercase tracking-widest rounded-full px-3 py-1.5">Social Feed Ads</span>
            </div>
            <ul className="space-y-5">
              {[
                ["1.7s", "average view before the scroll", EyeOff],
                ["42%", "of users run ad-blockers", EyeOff],
                ["1×", "impression per served ad", EyeOff],
                ["₹4+", "typical cost per impression (CPM)", Timer],
              ].map(([v, l]) => (
                <li key={l} className="flex items-baseline gap-4 border-b border-[#2E1065]/10 pb-4">
                  <span className="font-display font-black text-3xl text-[#2E1065]/40 shrink-0 w-28 line-through decoration-[#FF5A36]/60 decoration-2">{v}</span>
                  <span className="text-sm text-[#2E1065]/50">{l}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 bg-[#FBBF24] rounded-[2rem] p-8 sm:p-12 relative overflow-hidden"
          data-testid="reach-calculator"
        >
          <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#FF5A36]/25 blur-2xl" />
          <div className="relative grid lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-[#2E1065]" data-testid="calculator-title">
                Slide your pilot size. Watch your reach.
              </h3>
              <p className="mt-3 text-[#2E1065]/70 font-medium">
                Pilots start at 5,000 cups. Every figure updates live from the same
                assumptions — no hidden maths.
              </p>
              <div className="mt-8">
                <div className="flex items-end justify-between mb-3">
                  <span className="text-sm font-bold uppercase tracking-widest text-[#2E1065]/60">Cup quantity</span>
                  <span className="font-display font-black text-3xl text-[#2E1065]" data-testid="calculator-cups-value">{fmt(cups)}</span>
                </div>
                <input
                  type="range"
                  min="5000"
                  max="100000"
                  step="1000"
                  value={cups}
                  onChange={(e) => setCups(Number(e.target.value))}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer accent-[#FF5A36] bg-[#2E1065]/15"
                  data-testid="calculator-cups-slider"
                />
                <div className="flex justify-between text-xs font-bold text-[#2E1065]/50 mt-2">
                  <span>5K</span><span>50K</span><span>100K</span>
                </div>
              </div>

              <div className="mt-8 bg-[#2E1065] rounded-2xl p-6" data-testid="calculator-assumptions">
                <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-[#FBBF24] mb-4">
                  <Info className="w-4 h-4" /> Our assumptions — read these
                </p>
                <ul className="space-y-3">
                  {ASSUMPTIONS.map((a) => (
                    <li key={a} className="flex gap-2.5 text-xs sm:text-sm text-[#FAF7F2]/75 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A36] shrink-0 mt-1.5" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 content-start">
              {[
                { label: "Cost per cup (incl. print)", value: `₹${COST_PER_CUP.toFixed(2)}`, testid: "calc-cost-per-cup" },
                { label: "Estimated total investment", value: `₹${fmt(investment)}`, testid: "calc-cost" },
                { label: "Est. brand impressions", value: fmt(impressions), testid: "calc-impressions" },
                { label: "Est. hours of exposure", value: fmt(exposureHours), testid: "calc-exposure" },
                { label: "Est. cost per impression", value: `₹${costPerImpression.toFixed(2)}`, testid: "calc-cpi" },
              ].map((s) => (
                <motion.div
                  key={s.label}
                  layout
                  className="bg-[#FAF7F2] rounded-2xl p-5 shadow-sm"
                  data-testid={s.testid}
                >
                  <p className="font-display font-black text-2xl sm:text-3xl text-[#FF5A36]">{s.value}</p>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#2E1065]/60 mt-1">{s.label}</p>
                </motion.div>
              ))}
              <div className="col-span-2 text-xs font-semibold text-[#2E1065]/60 leading-relaxed" data-testid="calc-disclaimer">
                Exposure estimates follow the assumptions listed here. Cup quantity, cost per cup and
                investment are exact; impressions and hours are directional estimates for planning your pilot.
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16" data-testid="workflow-intro">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#FF5A36] mb-3">How It Works</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="workflow-steps">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-white rounded-3xl p-6 border border-[#2E1065]/10 hover:border-[#FF5A36]/40 hover:-translate-y-1.5 transition-all duration-300"
              data-testid={`workflow-step-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#2E1065] group-hover:bg-[#FF5A36] flex items-center justify-center transition-colors duration-300 mb-5">
                <s.icon className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <p className="font-display font-bold text-lg text-[#2E1065]">{s.title}</p>
              <p className="text-sm text-[#2E1065]/60 mt-2 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
